import { useMemo } from 'react'

import { useAppSelector } from '../store/hooks'
import { selectAccountId } from '../store/auth/authSlice'
import { useGetExternalUserByIdQuery } from '../store/externalUser/externalUser'
import { useGetAccountQuery } from '../store/accountUserSettings/accountUserSettings'
import { type IntegrationServiceId } from './integrations'
import { getAccountLinkedFolderId, getDistinctLinkedFolderIds } from './linkedDocumentFolders'

export const useContactLinkedFolder = (serviceId?: IntegrationServiceId, contactId?: string): string[] | undefined => {
  const accountId = useAppSelector(selectAccountId)

  const { data } = useGetExternalUserByIdQuery({ accountId, userId: contactId ?? '' }, { skip: !accountId || !contactId || !serviceId })
  const { data: account } = useGetAccountQuery({ accountId }, { skip: !accountId || !serviceId })

  return useMemo(() => {
    const contactFolderIds = getDistinctLinkedFolderIds(data?.data ? [data.data] : [], serviceId)
    if (contactFolderIds && contactFolderIds.length > 0) {
      return contactFolderIds
    }
    const accountFolderId = getAccountLinkedFolderId(account?.data, serviceId)
    return accountFolderId ? [accountFolderId] : undefined
  }, [data, account, serviceId])
}

export const useContactLinkedFoldersByServiceId = (
  serviceIds: IntegrationServiceId[],
  contactId?: string | null,
): { folderIdsByServiceId: Partial<Record<IntegrationServiceId, string[]>>; isLoading: boolean } => {
  const accountId = useAppSelector(selectAccountId)

  const { data, isLoading: isContactLoading } = useGetExternalUserByIdQuery(
    { accountId, userId: contactId ?? '' },
    { skip: !accountId || !contactId || serviceIds.length === 0 },
  )
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery({ accountId }, { skip: !accountId || serviceIds.length === 0 })

  const folderIdsByServiceId = useMemo(() => {
    const contact = data?.data
    const resolveForServiceId = (serviceId: IntegrationServiceId): string[] => {
      const contactFolderIds = getDistinctLinkedFolderIds(contact ? [contact] : [], serviceId)
      if (contactFolderIds && contactFolderIds.length > 0) {
        return contactFolderIds
      }
      const accountFolderId = getAccountLinkedFolderId(account?.data, serviceId)
      return accountFolderId ? [accountFolderId] : []
    }
    return Object.fromEntries(
      serviceIds.map((serviceId) => [serviceId, resolveForServiceId(serviceId)] as const).filter(([, ids]) => ids.length > 0),
    ) as Partial<Record<IntegrationServiceId, string[]>>
  }, [data, account, serviceIds])

  return { folderIdsByServiceId, isLoading: isContactLoading || isAccountLoading }
}
